/**
 * Two-Way Meeting Translation Tutorial Page (desktop app + KizunaAI)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app on Windows; the ones
 * not retaken for v0.44.0 are from v0.41.1.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Two-Way Voice Translation in Teams, Zoom and Other Meeting Apps',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'This guide sets up the Sokuji desktop app for a video call with someone who speaks another language. They hear what you say, spoken in their language. You hear what they say, translated into yours and read aloud, and you can read both sides as subtitles. It uses KizunaAI (Powered by Soniox; the Qwen engine works the same way) and works with any meeting app that lets you choose a microphone, including the Microsoft Teams and Zoom desktop apps. The screenshots are from Sokuji 0.44.0 on Windows with Microsoft Teams.',
  },
  steps: [
    {
      title: 'Set up Sokuji with KizunaAI',
      content:
        'If you have not done so yet, follow the <a href="/docs/kizuna-ai/quick-start">KizunaAI quick start</a> to install Sokuji and sign in.',
    },
    {
      title: 'Choose the two-way scenario',
      content:
        'In the setup guide, choose <strong>Two-way online conversation</strong>. It puts Sokuji in <strong>Both</strong> mode, speaks your translation into the meeting and reads the other side’s translation to you. If you have already finished the setup, click <strong>Both</strong> in the mode switch at the bottom left of the main window instead. The first time, a panel asks where the other side is: keep <strong>In a meeting</strong>. Clicking Both does not switch on <strong>Translation I hear</strong>; you do that in step 5.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Set the two languages',
      content:
        'In Settings, under <strong>Translation Languages</strong>, set <strong>I speak</strong> to your own language and <strong>they hear</strong> to the other person’s. The line underneath shows the other direction: it reads “They speak English → I read Chinese” until Sokuji listens to one app such as Teams (step 5), then “→ I hear Chinese”, because the other side’s translation is read aloud to you. For a call with a client in the UK, for example: I speak Chinese, they hear English.',
      screenshot: '02-languages.png',
      tip: 'Do not leave I speak on Auto Detect. In Both mode Sokuji has to know your language to translate the other side into it, and Start stays disabled until you choose one.',
    },
    {
      title: 'Install the virtual audio cable',
      content:
        'Your translated voice reaches the meeting through a virtual microphone. On Windows this is VB-CABLE, a free driver. While it is missing, Sokuji asks to install it each time it starts: click <strong>Install Now</strong>, and restart Windows if it asks you to. From then on, Sokuji sends its translated voice to VB-CABLE by itself.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'On macOS the Sokuji installer adds a virtual audio driver, and on Linux Sokuji creates its own virtual microphone. The rest of this guide is the same.',
    },
    {
      title: 'Choose your microphone, the meeting app and what you hear',
      content:
        'Still in Settings: under <strong>Microphone</strong>, pick your real microphone. Under <strong>Other’s audio</strong>, pick your meeting app, here <strong>Microsoft Teams</strong>. Sokuji then listens only to that app, so sound from other programs is not translated, and you keep hearing the other person’s own voice through Teams as usual. In the <strong>Speech</strong> block, <strong>Translation the other side hears</strong> goes to the virtual microphone, and <strong>Translation I hear</strong> reads the other person’s translation to you on the device beside it. Switch Translation I hear off if you would rather only read it.',
      screenshot: '04-devices.png',
      tip: 'If your meeting app is not in the list, join the call first (an app often appears only once it plays sound) and click the refresh icon. System Audio (All Applications) works too, but then Translation I hear is greyed out, so that Sokuji does not translate its own voice again: you only read the other side.',
    },
    {
      title: 'Choose VB-CABLE as the microphone in the meeting app',
      content:
        'In Microsoft Teams, open <strong>Settings → Devices</strong> and set the microphone to <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. In Zoom the same setting is under <strong>Settings → Audio → Microphone</strong>. Leave the speaker on your usual headset or speakers.',
      tip: 'Your real microphone is selected in Sokuji, not in the meeting app: Sokuji listens to you, and the meeting app receives only the translated voice.',
    },
    {
      title: 'Start translating',
      content:
        'Click <strong>Start</strong> in Sokuji. When you speak, the other side hears your words in their language through the meeting app, and Sokuji shows both sides as subtitles. When they speak, you hear their own voice through Teams, then Sokuji reads the translation to you in your language.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Tips for Best Results',
    items: [
      'Wear headphones. Otherwise your microphone picks up the other person from your speakers, and Sokuji translates them as if they were you.',
      'Make a short test call first, for example with a colleague, and check that they hear the translated voice.',
      'Pause briefly at the end of each sentence: the translation is spoken once you finish a thought.',
      'Hearing the other side read aloud costs extra. If subtitles are enough, switch off Translation I hear before you start: it cannot change while a session runs.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Does the other person need to install anything?',
        answer: 'No. Only you run Sokuji. The other person just hears a microphone that speaks their language.',
      },
      {
        question: 'Does this work with the Teams desktop app?',
        answer:
          'Yes. The Sokuji desktop app works with any meeting app that lets you choose a microphone, including the Teams and Zoom desktop apps.',
      },
      {
        question: 'The other person hears nothing. What should I check?',
        answer:
          'Check that the meeting app’s microphone is CABLE Output (VB-Audio Virtual Cable), that Translation the other side hears is switched on in Sokuji, and that Sokuji is in Me or Both mode with a session running.',
      },
      {
        question: 'The other person’s words are not translated. What should I check?',
        answer:
          'Check that Other’s audio is set to your meeting app or to System Audio (All Applications), that Sokuji is in Both mode with the other side in a meeting, and that the call is actually playing sound.',
      },
      {
        question: 'I read the other side’s translation but do not hear it. What should I check?',
        answer:
          'Check that Translation I hear is switched on and plays on the device you listen to, and that Other’s audio is your meeting app rather than System Audio (All Applications). Stop the session to change the switch.',
      },
      {
        question: 'Does translating both directions cost twice as much?',
        answer:
          'About that, by default: Both mode runs a separate session for each direction, which copes best when both people talk at once. With the Soniox engine, switching on Shared session in Both mode (Settings → Advanced → Provider) translates both sides in one session at lower cost; the Qwen engine always keeps the two sessions. Reading the other side aloud adds a second voice on top. The billing guide has the details.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '在 Teams、Zoom 等会议应用中进行双向语音翻译',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content:
      '本指南介绍如何设置 Sokuji 桌面应用，以便与说另一种语言的人进行视频通话。对方会听到你说的话被翻译成他们的语言；你会听到对方的话被翻译成你的语言并朗读出来，双方的内容也都会以字幕显示。本指南使用 KizunaAI（Soniox 驱动；Qwen 引擎的用法相同），适用于任何可以选择麦克风的会议应用，包括 Microsoft Teams 和 Zoom 桌面应用。截图来自 Windows 上的 Sokuji 0.44.0 与 Microsoft Teams。',
  },
  steps: [
    {
      title: '用 KizunaAI 设置 Sokuji',
      content:
        '如果还没有完成，请按照 <a href="/docs/kizuna-ai/quick-start">KizunaAI 快速入门</a>安装 Sokuji 并登录。',
    },
    {
      title: '选择双向场景',
      content:
        '在设置向导中选择<strong>在线双向对话</strong>。它会让 Sokuji 进入<strong>两者</strong>模式，把你的翻译说进会议，并把对方的翻译朗读给你听。如果已经完成设置，也可以点击主窗口左下角模式切换中的<strong>两者</strong>。第一次点击时会弹出一个面板，询问对方在哪里：保持<strong>在线会议</strong>即可。点击“两者”不会打开<strong>我听到的翻译</strong>，这一步在第 5 步中完成。',
      screenshot: '01-scenario.png',
    },
    {
      title: '设置两种语言',
      content:
        '在设置的<strong>翻译语言</strong>下，将<strong>我说</strong>设为你自己的语言，将<strong>对方听到</strong>设为对方的语言。下方那一行会显示另一个方向：在 Sokuji 收听某个应用（例如 Teams，见第 5 步）之前显示“对方说英语 → 我阅读中文”，之后显示“→ 我听到中文”，因为对方的翻译会朗读给你听。例如与英国客户通话时：我说中文，对方听到英语。',
      screenshot: '02-languages.png',
      tip: '不要让“我说”停留在“自动检测”。在两者模式下，Sokuji 需要知道你的语言才能把对方的话翻译过来；在你选定语言之前，“开始”按钮会一直不可用。',
    },
    {
      title: '安装虚拟音频线',
      content:
        '你的翻译语音通过虚拟麦克风传入会议。在 Windows 上，它就是免费驱动 VB-CABLE。只要尚未安装，Sokuji 每次启动都会询问是否安装：点击 <strong>Install Now</strong>，如果系统提示则重启 Windows。此后，Sokuji 会自动把翻译语音发送到 VB-CABLE。',
      screenshot: '03-virtual-cable-tip.png',
      tip: '在 macOS 上，Sokuji 安装程序会添加一个虚拟音频驱动；在 Linux 上，Sokuji 会自行创建虚拟麦克风。本指南的其余步骤相同。',
    },
    {
      title: '选择麦克风、会议应用和你要听的内容',
      content:
        '仍然在设置中：在<strong>麦克风</strong>下选择你的真实麦克风。在<strong>对方音频</strong>下选择你的会议应用，此处为 <strong>Microsoft Teams</strong>。这样 Sokuji 只会收听该应用，其他程序的声音不会被翻译，你仍然会像平常一样通过 Teams 听到对方的原声。在<strong>语音</strong>部分，<strong>对方听到的翻译</strong>输出到虚拟麦克风，<strong>我听到的翻译</strong>则在它旁边选择的设备上把对方的翻译朗读给你听。如果你只想阅读，请关闭“我听到的翻译”。',
      screenshot: '04-devices.png',
      tip: '如果列表中没有你的会议应用，请先加入通话（应用通常要在播放声音后才会出现），然后点击刷新图标。也可以选择 System Audio (All Applications)，但这时“我听到的翻译”会变灰，以免 Sokuji 把自己的声音再翻译一遍：你只能阅读对方的翻译。',
    },
    {
      title: '在会议应用中选择 VB-CABLE 作为麦克风',
      content:
        '在 Microsoft Teams 中打开<strong>设置 → 设备</strong>，将麦克风设为 <strong>CABLE Output (VB-Audio Virtual Cable)</strong>。在 Zoom 中，同样的设置位于<strong>设置 → 音频 → 麦克风</strong>。扬声器保持你平时使用的耳机或音箱即可。',
      tip: '真实麦克风是在 Sokuji 中选择的，而不是在会议应用中：Sokuji 收听你的声音，会议应用只接收翻译后的语音。',
    },
    {
      title: '开始翻译',
      content:
        '在 Sokuji 中点击<strong>开始</strong>。你说话时，对方会通过会议应用听到用他们的语言说出的你的话，Sokuji 会以字幕显示双方的内容。对方说话时，你会通过 Teams 听到他们的原声，随后 Sokuji 会用你的语言把翻译朗读给你听。',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: '最佳使用建议',
    items: [
      '请戴耳机。否则你的麦克风会拾取扬声器里对方的声音，Sokuji 会把它当作你的话来翻译。',
      '先进行一次简短的测试通话（例如和同事），确认对方能听到翻译语音。',
      '每句话结束时稍作停顿：一段话说完后，翻译才会被朗读出来。',
      '朗读对方的翻译会额外计费。如果字幕就够用，请在开始前关闭“我听到的翻译”：会话进行中无法更改。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '对方需要安装什么吗？',
        answer: '不需要。只有你运行 Sokuji。对方只会听到一个说着他们语言的麦克风。',
      },
      {
        question: '这能在 Teams 桌面应用中使用吗？',
        answer:
          '可以。Sokuji 桌面应用适用于任何可以选择麦克风的会议应用，包括 Teams 和 Zoom 桌面应用。',
      },
      {
        question: '对方什么也听不到，应该检查什么？',
        answer:
          '检查会议应用的麦克风是否为 CABLE Output (VB-Audio Virtual Cable)，Sokuji 中的“对方听到的翻译”是否已打开，以及 Sokuji 是否处于我或两者模式且会话正在运行。',
      },
      {
        question: '对方的话没有被翻译，应该检查什么？',
        answer:
          '检查“对方音频”是否设为你的会议应用或 System Audio (All Applications)，Sokuji 是否处于两者模式且对方在线会议，以及通话是否确实在播放声音。',
      },
      {
        question: '我能看到对方的翻译，却听不到，应该检查什么？',
        answer:
          '检查“我听到的翻译”是否已打开、是否在你正在听的设备上播放，以及“对方音频”是否为你的会议应用而不是 System Audio (All Applications)。要更改这个开关，请先停止会话。',
      },
      {
        question: '双向翻译的费用是否翻倍？',
        answer:
          '默认情况下大约如此：两者模式为每个方向各运行一个会话，在双方同时说话时最可靠。使用 Soniox 引擎时，打开“「两者」模式下共享会话”（设置 → 高级 → 提供商）后，一个会话同时翻译双方，费用更低；Qwen 引擎始终保持两个会话。朗读对方的翻译还会再增加一个声音的费用。详情请参阅余额与费用指南。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content:
      '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: 'Teams、Zoom などの会議アプリで双方向の音声翻訳',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content:
      'このガイドでは、別の言語を話す人とビデオ通話をするために Sokuji デスクトップアプリを設定します。相手には、あなたの話が相手の言語で音声として届きます。あなたには、相手の話があなたの言語に翻訳されて読み上げられ、双方の発言を字幕で読むこともできます。KizunaAI（Soniox 提供。Qwen エンジンでも同じ手順です）を使用し、Microsoft Teams や Zoom のデスクトップアプリを含め、マイクを選択できる会議アプリならどれでも使えます。スクリーンショットは Windows 版 Sokuji 0.44.0 と Microsoft Teams のものです。',
  },
  steps: [
    {
      title: 'KizunaAI で Sokuji をセットアップする',
      content:
        'まだの場合は、<a href="/docs/kizuna-ai/quick-start">KizunaAI クイックスタート</a>に沿って Sokuji をインストールし、サインインしてください。',
    },
    {
      title: '双方向のシナリオを選ぶ',
      content:
        'セットアップガイドで<strong>オンラインで双方向に会話</strong>を選びます。Sokuji が<strong>両方</strong>モードになり、あなたの翻訳を会議に音声で届け、相手の翻訳をあなたに読み上げます。すでにセットアップを終えている場合は、代わりにメイン画面左下のモード切り替えで<strong>両方</strong>をクリックします。初回は、相手がどこにいるかを尋ねるパネルが表示されるので、<strong>オンライン会議</strong>のままにします。「両方」をクリックしても<strong>自分に聞こえる翻訳</strong>はオンになりません。それはステップ 5 で行います。',
      screenshot: '01-scenario.png',
    },
    {
      title: '2 つの言語を設定する',
      content:
        '設定の<strong>翻訳言語</strong>で、<strong>話す言語</strong>を自分の言語に、<strong>相手が聞く言語</strong>を相手の言語に設定します。その下の行に逆方向が表示されます。Sokuji が Teams などの 1 つのアプリの音だけを聞くようになるまで（ステップ 5）は「相手は英語を話す → 自分は中国語で読む」、その後は相手の翻訳があなたに読み上げられるので「→ 自分は中国語で聞く」と表示されます。たとえば英国の顧客との通話なら、話す言語は中国語、相手が聞く言語は英語です。',
      screenshot: '02-languages.png',
      tip: '「話す言語」を「自動検出」のままにしないでください。両方モードでは、相手の発言をあなたの言語に翻訳するために Sokuji があなたの言語を知る必要があり、言語を選ぶまで「開始」は押せません。',
    },
    {
      title: '仮想オーディオケーブルをインストールする',
      content:
        '翻訳されたあなたの声は、仮想マイクを通じて会議に届きます。Windows では無料のドライバー VB-CABLE がその役割を担います。未インストールの間は、Sokuji が起動のたびにインストールを勧めます。<strong>Install Now</strong> をクリックし、求められたら Windows を再起動してください。以降は、Sokuji が翻訳音声を自動で VB-CABLE に送ります。',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'macOS では Sokuji のインストーラーが仮想オーディオドライバーを追加し、Linux では Sokuji が独自の仮想マイクを作成します。このガイドの残りの手順は同じです。',
    },
    {
      title: 'マイク、会議アプリ、自分に聞こえる内容を選ぶ',
      content:
        '引き続き設定で、<strong>マイクロフォン</strong>で実際のマイクを選びます。<strong>相手の音声</strong>で会議アプリ（ここでは <strong>Microsoft Teams</strong>）を選びます。これで Sokuji はそのアプリの音だけを聞くので、ほかのプログラムの音は翻訳されません。相手の生の声は、これまでどおり Teams から聞こえます。<strong>音声</strong>セクションでは、<strong>相手に聞こえる翻訳</strong>は仮想マイクに出力され、<strong>自分に聞こえる翻訳</strong>はその横のデバイスで相手の翻訳をあなたに読み上げます。字幕で読むだけでよい場合は、「自分に聞こえる翻訳」をオフにしてください。',
      screenshot: '04-devices.png',
      tip: '会議アプリが一覧にない場合は、先に通話に参加してから（アプリは音を再生して初めて表示されることがよくあります）更新アイコンをクリックしてください。System Audio (All Applications) も使えますが、その場合は Sokuji が自分の翻訳音声をもう一度翻訳しないよう「自分に聞こえる翻訳」がグレー表示になり、相手の発言は字幕で読むだけになります。',
    },
    {
      title: '会議アプリのマイクに VB-CABLE を選ぶ',
      content:
        'Microsoft Teams で<strong>設定 → デバイス</strong>を開き、マイクを <strong>CABLE Output (VB-Audio Virtual Cable)</strong> に設定します。Zoom では同じ設定が<strong>設定 → オーディオ → マイク</strong>にあります。スピーカーはいつものヘッドセットやスピーカーのままにしておきます。',
      tip: '実際のマイクは会議アプリではなく Sokuji で選びます。Sokuji があなたの声を聞き、会議アプリには翻訳された音声だけが届きます。',
    },
    {
      title: '翻訳を開始する',
      content:
        'Sokuji で<strong>開始</strong>をクリックします。あなたが話すと、相手には会議アプリを通じてあなたの言葉が相手の言語で聞こえ、Sokuji には双方の発言が字幕で表示されます。相手が話すと、まず Teams から相手の生の声が聞こえ、続いて Sokuji があなたの言語で翻訳を読み上げます。',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: '最良の結果を得るためのヒント',
    items: [
      'ヘッドホンを着けてください。着けないと、スピーカーから流れる相手の声をマイクが拾い、Sokuji がそれをあなたの発言として翻訳してしまいます。',
      'まず同僚などと短いテスト通話をして、翻訳音声が相手に聞こえるか確認してください。',
      '文の終わりで少し間を置いてください。ひとまとまりの話を終えた時点で翻訳が読み上げられます。',
      '相手の翻訳の読み上げには追加料金がかかります。字幕で十分な場合は、開始する前に「自分に聞こえる翻訳」をオフにしてください。セッションの実行中は変更できません。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: '相手も何かをインストールする必要がありますか？',
        answer: 'いいえ。Sokuji を動かすのはあなただけです。相手には、相手の言語を話すマイクの音声が聞こえるだけです。',
      },
      {
        question: 'Teams のデスクトップアプリでも使えますか？',
        answer:
          'はい。Sokuji デスクトップアプリは、Teams や Zoom のデスクトップアプリを含め、マイクを選択できる会議アプリならどれでも使えます。',
      },
      {
        question: '相手に何も聞こえません。何を確認すればよいですか？',
        answer:
          '会議アプリのマイクが CABLE Output (VB-Audio Virtual Cable) になっているか、Sokuji で「相手に聞こえる翻訳」がオンになっているか、Sokuji が自分モードまたは両方モードでセッションを実行中かを確認してください。',
      },
      {
        question: '相手の発言が翻訳されません。何を確認すればよいですか？',
        answer:
          '「相手の音声」が会議アプリまたは System Audio (All Applications) に設定されているか、Sokuji が両方モードで相手の場所が「オンライン会議」になっているか、通話で実際に音が再生されているかを確認してください。',
      },
      {
        question: '相手の翻訳は読めますが、聞こえません。何を確認すればよいですか？',
        answer:
          '「自分に聞こえる翻訳」がオンになっていて、あなたが聞いているデバイスで再生されるか、「相手の音声」が System Audio (All Applications) ではなく会議アプリになっているかを確認してください。このスイッチを変更するには、セッションを停止してください。',
      },
      {
        question: '双方向に翻訳すると料金は 2 倍になりますか？',
        answer:
          '既定ではおよそ 2 倍です。両方モードは方向ごとに別々のセッションを実行し、双方が同時に話す場合にいちばんうまく対応できます。Soniox エンジンでは、「「両方」モードでセッションを共有」（設定 → 詳細 → プロバイダー）をオンにすると、1 つのセッションで双方を翻訳し、料金を抑えられます。Qwen エンジンは常に 2 つのセッションのままです。相手の翻訳を読み上げると、さらに 2 つ目の音声の料金が加わります。詳しくは料金ガイドを参照してください。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content:
      'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: 'Teams, Zoom 등 회의 앱에서 양방향 음성 번역',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content:
      '이 가이드는 다른 언어를 쓰는 사람과 화상 통화를 할 수 있도록 Sokuji 데스크톱 앱을 설정합니다. 상대방은 내가 한 말을 자신의 언어로 된 음성으로 듣습니다. 나는 상대방의 말을 내 언어로 번역된 음성으로 듣고, 양쪽의 말을 자막으로도 읽을 수 있습니다. KizunaAI(Soniox 제공이며, Qwen 엔진도 같은 방식으로 작동합니다)를 사용하며, Microsoft Teams와 Zoom 데스크톱 앱을 포함해 마이크를 선택할 수 있는 모든 회의 앱에서 작동합니다. 스크린샷은 Windows용 Sokuji 0.44.0과 Microsoft Teams에서 촬영했습니다.',
  },
  steps: [
    {
      title: 'KizunaAI로 Sokuji 설정',
      content:
        '아직 하지 않았다면 <a href="/docs/kizuna-ai/quick-start">KizunaAI 빠른 시작</a>을 따라 Sokuji를 설치하고 로그인하세요.',
    },
    {
      title: '양방향 시나리오 선택',
      content:
        '설정 가이드에서 <strong>양방향 온라인 대화</strong>를 선택하세요. Sokuji가 <strong>모두</strong> 모드로 설정되어, 내 번역을 회의에 음성으로 전달하고 상대방의 번역을 나에게 읽어 줍니다. 이미 설정을 마쳤다면 대신 메인 창 왼쪽 아래의 모드 전환에서 <strong>모두</strong>를 클릭하세요. 처음에는 상대방이 어디에 있는지 묻는 패널이 나타납니다. <strong>온라인 회의</strong>를 그대로 두세요. “모두”를 클릭해도 <strong>내가 듣는 번역</strong>은 켜지지 않습니다. 이는 5단계에서 켭니다.',
      screenshot: '01-scenario.png',
    },
    {
      title: '두 언어 설정',
      content:
        '설정의 <strong>번역 언어</strong>에서 <strong>내가 말하는 언어</strong>를 내 언어로, <strong>상대방이 듣는 언어</strong>를 상대방의 언어로 설정하세요. 아래 줄에 반대 방향이 표시됩니다. Sokuji가 Teams 같은 앱 하나의 소리만 듣게 하기 전까지는(5단계) “상대방은 영어로 말함 → 나는 중국어로 읽음”으로 표시되고, 그 뒤에는 상대방의 번역을 나에게 소리 내어 읽어 주므로 “→ 나는 중국어로 들음”으로 바뀝니다. 예를 들어 영국 고객과 통화한다면 내가 말하는 언어는 중국어, 상대방이 듣는 언어는 영어입니다.',
      screenshot: '02-languages.png',
      tip: '“내가 말하는 언어”를 “자동 감지”로 두지 마세요. 모두 모드에서는 상대방의 말을 내 언어로 번역하려면 Sokuji가 내 언어를 알아야 하므로, 언어를 고를 때까지 “시작”이 비활성화됩니다.',
    },
    {
      title: '가상 오디오 케이블 설치',
      content:
        '번역된 내 음성은 가상 마이크를 통해 회의에 전달됩니다. Windows에서는 무료 드라이버인 VB-CABLE이 이 역할을 합니다. 설치되지 않은 동안에는 Sokuji가 시작될 때마다 설치를 권합니다. <strong>Install Now</strong>를 클릭하고, 요청하면 Windows를 다시 시작하세요. 그 이후로는 Sokuji가 번역된 음성을 알아서 VB-CABLE로 보냅니다.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'macOS에서는 Sokuji 설치 프로그램이 가상 오디오 드라이버를 추가하고, Linux에서는 Sokuji가 자체 가상 마이크를 만듭니다. 이 가이드의 나머지 단계는 같습니다.',
    },
    {
      title: '마이크, 회의 앱, 들을 내용 선택',
      content:
        '계속 설정에서 <strong>마이크</strong>에 실제 마이크를 고르세요. <strong>상대방 오디오</strong>에는 회의 앱(여기서는 <strong>Microsoft Teams</strong>)을 고르세요. 그러면 Sokuji는 그 앱의 소리만 들으므로 다른 프로그램의 소리는 번역되지 않습니다. 상대방의 원래 목소리는 평소처럼 Teams로 계속 들립니다. <strong>음성</strong> 섹션에서 <strong>상대방에게 들리는 번역</strong>은 가상 마이크로 보내지고, <strong>내가 듣는 번역</strong>은 옆에서 고른 장치로 상대방의 번역을 나에게 읽어 줍니다. 읽기만 하고 싶다면 “내가 듣는 번역”을 끄세요.',
      screenshot: '04-devices.png',
      tip: '회의 앱이 목록에 없다면 먼저 통화에 참여한 다음(앱은 소리를 재생해야 나타나는 경우가 많습니다) 새로고침 아이콘을 클릭하세요. System Audio (All Applications)도 쓸 수 있지만, 그러면 Sokuji가 자기 음성을 다시 번역하지 않도록 “내가 듣는 번역”이 회색으로 비활성화되어 상대방의 말은 읽기만 할 수 있습니다.',
    },
    {
      title: '회의 앱에서 VB-CABLE을 마이크로 선택',
      content:
        'Microsoft Teams에서 <strong>설정 → 장치</strong>를 열고 마이크를 <strong>CABLE Output (VB-Audio Virtual Cable)</strong>로 설정하세요. Zoom에서는 같은 설정이 <strong>설정 → 오디오 → 마이크</strong>에 있습니다. 스피커는 평소 쓰는 헤드셋이나 스피커 그대로 두세요.',
      tip: '실제 마이크는 회의 앱이 아니라 Sokuji에서 선택합니다. Sokuji가 내 목소리를 듣고, 회의 앱은 번역된 음성만 받습니다.',
    },
    {
      title: '번역 시작',
      content:
        'Sokuji에서 <strong>시작</strong>을 클릭하세요. 내가 말하면 상대방은 회의 앱을 통해 내 말을 자신의 언어로 듣고, Sokuji는 양쪽의 말을 자막으로 보여 줍니다. 상대방이 말하면 나는 Teams로 상대방의 원래 목소리를 듣고, 이어서 Sokuji가 번역을 내 언어로 읽어 줍니다.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: '최상의 결과를 위한 팁',
    items: [
      '헤드폰을 착용하세요. 그렇지 않으면 마이크가 스피커에서 나오는 상대방의 목소리를 받아, Sokuji가 그것을 내 말처럼 번역합니다.',
      '먼저 동료 등과 짧은 테스트 통화를 해서 상대방에게 번역된 음성이 들리는지 확인하세요.',
      '각 문장이 끝날 때 잠깐 멈추세요. 하나의 생각을 마쳐야 번역이 음성으로 나옵니다.',
      '상대방의 번역을 소리 내어 들으면 추가 비용이 듭니다. 자막으로 충분하다면 시작하기 전에 “내가 듣는 번역”을 끄세요. 세션이 실행되는 동안에는 바꿀 수 없습니다.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '상대방도 무언가를 설치해야 하나요?',
        answer: '아니요. Sokuji는 나만 실행합니다. 상대방은 자신의 언어로 말하는 마이크 소리를 들을 뿐입니다.',
      },
      {
        question: 'Teams 데스크톱 앱에서도 되나요?',
        answer:
          '네. Sokuji 데스크톱 앱은 Teams와 Zoom 데스크톱 앱을 포함해 마이크를 선택할 수 있는 모든 회의 앱에서 작동합니다.',
      },
      {
        question: '상대방에게 아무것도 들리지 않습니다. 무엇을 확인해야 하나요?',
        answer:
          '회의 앱의 마이크가 CABLE Output (VB-Audio Virtual Cable)로 되어 있는지, Sokuji에서 “상대방에게 들리는 번역”이 켜져 있는지, Sokuji가 나 모드 또는 모두 모드이고 세션이 실행 중인지 확인하세요.',
      },
      {
        question: '상대방의 말이 번역되지 않습니다. 무엇을 확인해야 하나요?',
        answer:
          '“상대방 오디오”가 회의 앱 또는 System Audio (All Applications)로 설정되어 있는지, Sokuji가 모두 모드이고 “상대방 위치”가 “온라인 회의”로 되어 있는지, 통화에서 실제로 소리가 재생되고 있는지 확인하세요.',
      },
      {
        question: '상대방의 번역은 읽을 수 있지만 들리지 않습니다. 무엇을 확인해야 하나요?',
        answer:
          '“내가 듣는 번역”이 켜져 있고 내가 듣고 있는 장치로 재생되는지, “상대방 오디오”가 System Audio (All Applications)가 아니라 회의 앱으로 되어 있는지 확인하세요. 이 스위치를 바꾸려면 세션을 중지하세요.',
      },
      {
        question: '양방향으로 번역하면 비용이 두 배가 되나요?',
        answer:
          '기본값으로는 대략 그렇습니다. 모두 모드는 방향마다 별도의 세션을 실행하며, 두 사람이 동시에 말할 때 가장 잘 대응합니다. Soniox 엔진에서 “모두 모드에서 세션 공유”(설정 → 고급 → 제공자)를 켜면 하나의 세션이 양쪽을 번역하므로 비용이 더 낮습니다. Qwen 엔진은 항상 두 세션을 유지합니다. 상대방의 번역을 소리 내어 읽어 주면 그 위에 두 번째 음성이 더해집니다. 자세한 내용은 잔액 및 비용 가이드를 참고하세요.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content:
      '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Traducción de voz bidireccional en Teams, Zoom y otras aplicaciones de reuniones',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content:
      'Esta guía configura la aplicación de escritorio de Sokuji para una videollamada con alguien que habla otro idioma. La otra persona oye lo que dices, hablado en su idioma. Tú oyes lo que dice, traducido al tuyo y leído en voz alta, y puedes leer ambos lados como subtítulos. Usa KizunaAI (Con tecnología de Soniox; el motor de Qwen funciona igual) y funciona con cualquier aplicación de reuniones que te deje elegir un micrófono, incluidas las aplicaciones de escritorio de Microsoft Teams y Zoom. Las capturas son de Sokuji 0.44.0 en Windows con Microsoft Teams.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content:
        'Si aún no lo has hecho, sigue el <a href="/docs/kizuna-ai/quick-start">inicio rápido de KizunaAI</a> para instalar Sokuji e iniciar sesión.',
    },
    {
      title: 'Elige el escenario bidireccional',
      content:
        'En la guía de configuración, elige <strong>Conversación en línea bidireccional</strong>. Pone Sokuji en modo <strong>Ambos</strong>, pronuncia tu traducción en la reunión y te lee la traducción de la otra parte. Si ya terminaste la configuración, haz clic en <strong>Ambos</strong> en el selector de modo, abajo a la izquierda de la ventana principal. La primera vez, un panel te pregunta dónde está la otra parte: deja <strong>En una reunión</strong>. Hacer clic en Ambos no activa <strong>Traducción que yo oigo</strong>; eso lo haces en el paso 5.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Configura los dos idiomas',
      content:
        'En Configuración, en <strong>Idiomas de Traducción</strong>, pon en <strong>Yo hablo</strong> tu propio idioma y en <strong>ellos escuchan</strong> el de la otra persona. La línea de debajo muestra la otra dirección: dice «Ellos hablan Inglés → yo leo Chino» hasta que Sokuji escuche una sola aplicación, como Teams (paso 5), y entonces «→ yo escucho Chino», porque la traducción de la otra parte se te lee en voz alta. Por ejemplo, para una llamada con un cliente del Reino Unido: Yo hablo Chino, ellos escuchan Inglés.',
      screenshot: '02-languages.png',
      tip: 'No dejes Yo hablo en Detección Automática. En el modo Ambos, Sokuji necesita saber tu idioma para traducir a él lo que dice la otra parte, e Iniciar sigue desactivado hasta que elijas uno.',
    },
    {
      title: 'Instala el cable de audio virtual',
      content:
        'Tu voz traducida llega a la reunión a través de un micrófono virtual. En Windows es VB-CABLE, un controlador gratuito. Mientras no esté instalado, Sokuji te ofrece instalarlo cada vez que arranca: haz clic en <strong>Install Now</strong> y reinicia Windows si te lo pide. A partir de entonces, Sokuji envía su voz traducida a VB-CABLE por sí solo.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'En macOS, el instalador de Sokuji añade un controlador de audio virtual, y en Linux Sokuji crea su propio micrófono virtual. El resto de esta guía es igual.',
    },
    {
      title: 'Elige tu micrófono, la aplicación de reuniones y lo que oyes',
      content:
        'Todavía en Configuración: en <strong>Micrófono</strong>, elige tu micrófono real. En <strong>Audio del otro</strong>, elige tu aplicación de reuniones, aquí <strong>Microsoft Teams</strong>. Así Sokuji escucha solo esa aplicación, de modo que el sonido de otros programas no se traduce, y sigues oyendo la voz original de la otra persona a través de Teams como siempre. En la sección <strong>Voz</strong>, <strong>Traducción que oye la otra parte</strong> va al micrófono virtual, y <strong>Traducción que yo oigo</strong> te lee la traducción de la otra persona en el dispositivo elegido a su lado. Desactiva Traducción que yo oigo si prefieres solo leerla.',
      screenshot: '04-devices.png',
      tip: 'Si tu aplicación de reuniones no aparece en la lista, únete primero a la llamada (a menudo una aplicación solo aparece cuando reproduce sonido) y haz clic en el icono de actualizar. System Audio (All Applications) también funciona, pero entonces Traducción que yo oigo aparece en gris, para que Sokuji no vuelva a traducir su propia voz: solo lees lo que dice la otra parte.',
    },
    {
      title: 'Elige VB-CABLE como micrófono en la aplicación de reuniones',
      content:
        'En Microsoft Teams, abre <strong>Configuración → Dispositivos</strong> y elige como micrófono <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. En Zoom, el mismo ajuste está en <strong>Configuración → Audio → Micrófono</strong>. Deja el altavoz en tus auriculares o altavoces habituales.',
      tip: 'Tu micrófono real se elige en Sokuji, no en la aplicación de reuniones: Sokuji te escucha a ti, y la aplicación de reuniones recibe solo la voz traducida.',
    },
    {
      title: 'Empieza a traducir',
      content:
        'Haz clic en <strong>Iniciar</strong> en Sokuji. Cuando hablas, la otra parte oye tus palabras en su idioma a través de la aplicación de reuniones, y Sokuji muestra ambos lados como subtítulos. Cuando habla la otra persona, oyes su propia voz a través de Teams y después Sokuji te lee la traducción en tu idioma.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Consejos para obtener mejores resultados',
    items: [
      'Usa auriculares. Si no, tu micrófono capta a la otra persona desde tus altavoces, y Sokuji la traduce como si fueras tú.',
      'Haz antes una llamada de prueba corta, por ejemplo con un compañero, y comprueba que oye la voz traducida.',
      'Haz una breve pausa al final de cada frase: la traducción se pronuncia cuando terminas una idea.',
      'Oír en voz alta la traducción de la otra parte cuesta más. Si te bastan los subtítulos, desactiva Traducción que yo oigo antes de empezar: no se puede cambiar mientras hay una sesión en marcha.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿La otra persona tiene que instalar algo?',
        answer: 'No. Solo tú usas Sokuji. La otra persona simplemente oye un micrófono que habla su idioma.',
      },
      {
        question: '¿Funciona con la aplicación de escritorio de Teams?',
        answer:
          'Sí. La aplicación de escritorio de Sokuji funciona con cualquier aplicación de reuniones que te deje elegir un micrófono, incluidas las aplicaciones de escritorio de Teams y Zoom.',
      },
      {
        question: 'La otra persona no oye nada. ¿Qué debo comprobar?',
        answer:
          'Comprueba que el micrófono de la aplicación de reuniones sea CABLE Output (VB-Audio Virtual Cable), que Traducción que oye la otra parte esté activada en Sokuji y que Sokuji esté en modo Yo o Ambos con una sesión en marcha.',
      },
      {
        question: 'Lo que dice la otra persona no se traduce. ¿Qué debo comprobar?',
        answer:
          'Comprueba que Audio del otro esté configurado con tu aplicación de reuniones o con System Audio (All Applications), que Sokuji esté en modo Ambos con la otra parte en una reunión y que la llamada esté reproduciendo sonido de verdad.',
      },
      {
        question: 'Leo la traducción de la otra parte, pero no la oigo. ¿Qué debo comprobar?',
        answer:
          'Comprueba que Traducción que yo oigo esté activada y se reproduzca en el dispositivo por el que escuchas, y que Audio del otro sea tu aplicación de reuniones y no System Audio (All Applications). Detén la sesión para cambiar el interruptor.',
      },
      {
        question: '¿Traducir en ambas direcciones cuesta el doble?',
        answer:
          'Más o menos, de forma predeterminada: el modo Ambos ejecuta una sesión independiente para cada dirección, lo que funciona mejor cuando ambas personas hablan a la vez. Con el motor de Soniox, si activas Sesión compartida en el modo Ambos (Configuración → Avanzado → Proveedor), ambos lados se traducen en una sola sesión con menor coste; el motor de Qwen mantiene siempre las dos sesiones. Leer en voz alta la traducción de la otra parte añade además una segunda voz. La guía de saldo y costes tiene los detalles.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content:
      '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Traduction vocale bidirectionnelle dans Teams, Zoom et d’autres applications de réunion',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content:
      'Ce guide configure l’application de bureau Sokuji pour un appel vidéo avec quelqu’un qui parle une autre langue. Votre interlocuteur entend ce que vous dites, prononcé dans sa langue. Vous entendez ce qu’il dit, traduit dans la vôtre et lu à voix haute, et vous pouvez lire les deux côtés en sous-titres. Il utilise KizunaAI (Propulsé par Soniox ; le moteur Qwen fonctionne de la même façon) et fonctionne avec toute application de réunion qui vous laisse choisir un microphone, y compris les applications de bureau Microsoft Teams et Zoom. Les captures d’écran proviennent de Sokuji 0.44.0 sous Windows avec Microsoft Teams.',
  },
  steps: [
    {
      title: 'Configurez Sokuji avec KizunaAI',
      content:
        'Si ce n’est pas encore fait, suivez le <a href="/docs/kizuna-ai/quick-start">démarrage rapide KizunaAI</a> pour installer Sokuji et vous connecter.',
    },
    {
      title: 'Choisissez le scénario bidirectionnel',
      content:
        'Dans le guide de configuration, choisissez <strong>Conversation en ligne bidirectionnelle</strong>. Sokuji passe alors en mode <strong>Les deux</strong>, prononce votre traduction dans la réunion et vous lit la traduction de l’autre partie. Si vous avez déjà terminé la configuration, cliquez plutôt sur <strong>Les deux</strong> dans le sélecteur de mode, en bas à gauche de la fenêtre principale. La première fois, un panneau vous demande où se trouve l’autre partie : gardez <strong>En réunion</strong>. Cliquer sur Les deux n’active pas <strong>Traduction que j\'entends</strong> ; vous le ferez à l’étape 5.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Réglez les deux langues',
      content:
        'Dans les Paramètres, sous <strong>Langues de Traduction</strong>, réglez <strong>je parle</strong> sur votre propre langue et <strong>ils entendent</strong> sur celle de votre interlocuteur. La ligne en dessous indique l’autre sens : elle affiche « Ils parlent Anglais → je lis Chinois » jusqu’à ce que Sokuji n’écoute qu’une seule application, comme Teams (étape 5), puis « → j\'entends Chinois », car la traduction de l’autre partie vous est alors lue à voix haute. Par exemple, pour un appel avec un client au Royaume-Uni : je parle chinois, ils entendent anglais.',
      screenshot: '02-languages.png',
      tip: 'Ne laissez pas je parle sur Détection Automatique. En mode Les deux, Sokuji doit connaître votre langue pour y traduire les paroles de l’autre partie, et Démarrer reste désactivé tant que vous n’en avez pas choisi une.',
    },
    {
      title: 'Installez le câble audio virtuel',
      content:
        'Votre voix traduite arrive dans la réunion par un microphone virtuel. Sous Windows, il s’agit de VB-CABLE, un pilote gratuit. Tant qu’il est absent, Sokuji propose de l’installer à chaque démarrage : cliquez sur <strong>Install Now</strong>, puis redémarrez Windows si on vous le demande. Ensuite, Sokuji envoie de lui-même sa voix traduite vers VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'Sous macOS, le programme d’installation de Sokuji ajoute un pilote audio virtuel, et sous Linux Sokuji crée son propre microphone virtuel. La suite de ce guide est identique.',
    },
    {
      title: 'Choisissez votre microphone, l’application de réunion et ce que vous entendez',
      content:
        'Toujours dans les Paramètres : sous <strong>Microphone</strong>, sélectionnez votre vrai microphone. Sous <strong>Audio de l\'autre</strong>, sélectionnez votre application de réunion, ici <strong>Microsoft Teams</strong>. Sokuji n’écoute alors que cette application, et le son des autres programmes n’est pas traduit. Vous continuez d’entendre la voix de votre interlocuteur dans Teams, comme d’habitude. Dans la section <strong>Voix</strong>, <strong>Traduction entendue par l\'autre</strong> est envoyée au microphone virtuel, et <strong>Traduction que j\'entends</strong> vous lit la traduction de votre interlocuteur sur l’appareil choisi à côté. Désactivez « Traduction que j\'entends » si vous préférez seulement la lire.',
      screenshot: '04-devices.png',
      tip: 'Si votre application de réunion n’apparaît pas dans la liste, rejoignez d’abord l’appel (une application n’apparaît souvent qu’une fois qu’elle émet du son) et cliquez sur l’icône d’actualisation. System Audio (All Applications) fonctionne aussi, mais « Traduction que j\'entends » est alors grisée, pour que Sokuji ne retraduise pas sa propre voix : vous lisez seulement l’autre partie.',
    },
    {
      title: 'Choisissez VB-CABLE comme microphone dans l’application de réunion',
      content:
        'Dans Microsoft Teams, ouvrez <strong>Paramètres → Appareils</strong> et réglez le microphone sur <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. Dans Zoom, le même réglage se trouve sous <strong>Paramètres → Audio → Microphone</strong>. Laissez le haut-parleur sur votre casque ou vos enceintes habituels.',
      tip: 'Votre vrai microphone se choisit dans Sokuji, pas dans l’application de réunion : Sokuji vous écoute, et l’application de réunion ne reçoit que la voix traduite.',
    },
    {
      title: 'Lancez la traduction',
      content:
        'Cliquez sur <strong>Démarrer</strong> dans Sokuji. Quand vous parlez, votre interlocuteur entend vos paroles dans sa langue via l’application de réunion, et Sokuji affiche les deux côtés en sous-titres. Quand il parle, vous entendez sa propre voix dans Teams, puis Sokuji vous lit la traduction dans votre langue.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Conseils pour de meilleurs résultats',
    items: [
      'Portez un casque. Sinon, votre microphone capte votre interlocuteur depuis vos haut-parleurs, et Sokuji le traduit comme si c’était vous.',
      'Faites d’abord un court appel de test, par exemple avec un collègue, et vérifiez qu’il entend la voix traduite.',
      'Marquez une courte pause à la fin de chaque phrase : la traduction est prononcée une fois votre idée terminée.',
      'Entendre l’autre partie à voix haute coûte plus cher. Si les sous-titres suffisent, désactivez « Traduction que j\'entends » avant de démarrer : ce réglage ne peut pas changer pendant une session.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Mon interlocuteur doit-il installer quelque chose ?',
        answer: 'Non. Vous êtes le seul à utiliser Sokuji. Votre interlocuteur entend simplement un microphone qui parle sa langue.',
      },
      {
        question: 'Cela fonctionne-t-il avec l’application de bureau Teams ?',
        answer:
          'Oui. L’application de bureau Sokuji fonctionne avec toute application de réunion qui vous laisse choisir un microphone, y compris les applications de bureau Teams et Zoom.',
      },
      {
        question: 'Mon interlocuteur n’entend rien. Que dois-je vérifier ?',
        answer:
          'Vérifiez que le microphone de l’application de réunion est CABLE Output (VB-Audio Virtual Cable), que « Traduction entendue par l\'autre » est activée dans Sokuji, et que Sokuji est en mode Moi ou Les deux avec une session en cours.',
      },
      {
        question: 'Les paroles de mon interlocuteur ne sont pas traduites. Que dois-je vérifier ?',
        answer:
          'Vérifiez que Audio de l\'autre est réglé sur votre application de réunion ou sur System Audio (All Applications), que Sokuji est en mode Les deux avec l’autre partie en réunion, et que l’appel émet bien du son.',
      },
      {
        question: 'Je lis la traduction de l’autre partie, mais je ne l’entends pas. Que dois-je vérifier ?',
        answer:
          'Vérifiez que « Traduction que j\'entends » est activée et lue sur l’appareil que vous écoutez, et que Audio de l\'autre est votre application de réunion plutôt que System Audio (All Applications). Arrêtez la session pour changer ce réglage.',
      },
      {
        question: 'Traduire dans les deux sens coûte-t-il deux fois plus cher ?',
        answer:
          'À peu près, par défaut : le mode Les deux lance une session distincte pour chaque sens, ce qui gère le mieux les moments où les deux personnes parlent en même temps. Avec le moteur Soniox, activer « Session partagée en mode Les deux » (Paramètres → Avancé → Fournisseur) traduit les deux côtés dans une seule session, pour un coût réduit ; le moteur Qwen garde toujours les deux sessions. La lecture à voix haute de l’autre partie ajoute une deuxième voix en plus. Le guide sur le solde et les coûts donne les détails.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content:
      'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'ترجمة صوتية ثنائية الاتجاه في Teams وZoom وتطبيقات الاجتماعات الأخرى',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content:
      'يُعدّ هذا الدليل تطبيق Sokuji لسطح المكتب لإجراء مكالمة فيديو مع شخص يتحدث لغة أخرى. يسمع الطرف الآخر ما تقوله منطوقًا بلغته. وتسمع أنت ما يقوله مترجمًا إلى لغتك ومقروءًا بصوت مسموع، ويمكنك قراءة كلام الطرفين في صورة ترجمة نصية. يستخدم الدليل KizunaAI (مدعوم من Soniox؛ ويعمل محرك Qwen بالطريقة نفسها) ويعمل مع أي تطبيق اجتماعات يتيح لك اختيار الميكروفون، بما في ذلك تطبيقَي Microsoft Teams وZoom لسطح المكتب. لقطات الشاشة مأخوذة من Sokuji 0.44.0 على Windows مع Microsoft Teams.',
  },
  steps: [
    {
      title: 'اضبط Sokuji مع KizunaAI',
      content:
        'إذا لم تفعل ذلك بعد، فاتبع <a href="/docs/kizuna-ai/quick-start">البدء السريع مع KizunaAI</a> لتثبيت Sokuji وتسجيل الدخول.',
    },
    {
      title: 'اختر السيناريو ثنائي الاتجاه',
      content:
        'في دليل الإعداد، اختر <strong>محادثة عبر الإنترنت بالاتجاهين</strong>. يضع ذلك Sokuji في وضع <strong>الكل</strong>، فينطق ترجمتك في الاجتماع ويقرأ عليك ترجمة الطرف الآخر. إذا كنت قد أنهيت الإعداد بالفعل، فانقر بدلًا من ذلك على <strong>الكل</strong> في مبدّل الأوضاع أسفل يسار النافذة الرئيسية. في المرة الأولى، تسألك لوحة عن مكان الطرف الآخر: أبقِ الاختيار على <strong>في اجتماع</strong>. النقر على «الكل» لا يفعّل <strong>الترجمة التي أسمعها</strong>؛ ستفعل ذلك في الخطوة 5.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'اضبط اللغتين',
      content:
        'في الإعدادات، تحت <strong>لغات الترجمة</strong>، اضبط <strong>أتحدث</strong> على لغتك و<strong>يسمعون</strong> على لغة الطرف الآخر. يعرض السطر أسفلهما الاتجاه الآخر: يقول «يتحدثون الإنجليزية ← أقرأ الصينية» إلى أن يستمع Sokuji إلى تطبيق واحد مثل Teams (الخطوة 5)، ثم «← أسمع الصينية»، لأن ترجمة الطرف الآخر تُقرأ عليك بصوت مسموع. على سبيل المثال، لمكالمة مع عميل في المملكة المتحدة: أتحدث الصينية، يسمعون الإنجليزية.',
      screenshot: '02-languages.png',
      tip: 'لا تترك «أتحدث» على «اكتشاف تلقائي». في وضع الكل يجب أن يعرف Sokuji لغتك ليترجم كلام الطرف الآخر إليها، ويبقى زر «ابدأ» معطّلًا حتى تختار لغة.',
    },
    {
      title: 'ثبّت كابل الصوت الافتراضي',
      content:
        'يصل صوتك المترجَم إلى الاجتماع عبر ميكروفون افتراضي. على Windows هذا هو VB-CABLE، وهو برنامج تشغيل مجاني. ما دام غير مثبّت، يطلب Sokuji تثبيته في كل مرة يبدأ فيها: انقر على <strong>Install Now</strong>، وأعد تشغيل Windows إذا طُلب منك ذلك. بعد ذلك يرسل Sokuji صوته المترجَم إلى VB-CABLE تلقائيًا.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'على macOS يضيف مثبّت Sokuji برنامج تشغيل صوت افتراضيًا، وعلى Linux ينشئ Sokuji ميكروفونه الافتراضي الخاص. بقية هذا الدليل متطابقة.',
    },
    {
      title: 'اختر الميكروفون وتطبيق الاجتماع وما تسمعه',
      content:
        'ما زلت في الإعدادات: تحت <strong>ميكروفون</strong>، اختر ميكروفونك الحقيقي. وتحت <strong>صوت الآخر</strong>، اختر تطبيق الاجتماع، وهو هنا <strong>Microsoft Teams</strong>. عندها يستمع Sokuji إلى هذا التطبيق فقط، فلا يُترجَم صوت البرامج الأخرى، وتظل تسمع صوت الطرف الآخر الأصلي عبر Teams كالمعتاد. في قسم <strong>الكلام</strong>، تُوجَّه <strong>الترجمة التي يسمعها الطرف الآخر</strong> إلى الميكروفون الافتراضي، وتقرأ عليك <strong>الترجمة التي أسمعها</strong> ترجمة الطرف الآخر على الجهاز المختار بجانبها. أوقف «الترجمة التي أسمعها» إذا كنت تفضّل قراءتها فقط.',
      screenshot: '04-devices.png',
      tip: 'إذا لم يظهر تطبيق الاجتماع في القائمة، فانضم إلى المكالمة أولًا (غالبًا لا يظهر التطبيق إلا بعد أن يشغّل صوتًا) وانقر على أيقونة التحديث. يعمل System Audio (All Applications) أيضًا، لكن «الترجمة التي أسمعها» تصبح عندها رمادية، حتى لا يترجم Sokuji صوته هو مرة أخرى: فتكتفي بقراءة ترجمة الطرف الآخر.',
    },
    {
      title: 'اختر VB-CABLE ميكروفونًا في تطبيق الاجتماع',
      content:
        'في Microsoft Teams، افتح <strong>الإعدادات ← الأجهزة (Settings → Devices)</strong> واضبط الميكروفون على <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. في Zoom يوجد الإعداد نفسه تحت <strong>الإعدادات ← الصوت ← الميكروفون (Settings → Audio → Microphone)</strong>. اترك مكبر الصوت على سماعة الرأس أو السماعات المعتادة.',
      tip: 'يُختار ميكروفونك الحقيقي في Sokuji، لا في تطبيق الاجتماع: يستمع Sokuji إليك، ويتلقى تطبيق الاجتماع الصوت المترجَم فقط.',
    },
    {
      title: 'ابدأ الترجمة',
      content:
        'انقر على <strong>ابدأ</strong> في Sokuji. عندما تتحدث، يسمع الطرف الآخر كلماتك بلغته عبر تطبيق الاجتماع، ويعرض Sokuji كلام الطرفين كترجمة نصية. وعندما يتحدث هو، تسمع صوته الأصلي عبر Teams، ثم يقرأ عليك Sokuji الترجمة بلغتك.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'نصائح لأفضل النتائج',
    items: [
      'ارتدِ سماعات الرأس. وإلا فسيلتقط ميكروفونك صوت الطرف الآخر من مكبرات الصوت، وسيترجمه Sokuji كأنه كلامك.',
      'أجرِ أولًا مكالمة اختبار قصيرة، مع زميل مثلًا، وتأكد من أنه يسمع الصوت المترجَم.',
      'توقف لحظة في نهاية كل جملة: تُنطق الترجمة بعد أن تُكمل فكرتك.',
      'سماع ترجمة الطرف الآخر بصوت مسموع يكلّف أكثر. إذا كانت الترجمة النصية تكفيك، فأوقف «الترجمة التي أسمعها» قبل أن تبدأ: لا يمكن تغييرها أثناء تشغيل الجلسة.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'هل يحتاج الطرف الآخر إلى تثبيت أي شيء؟',
        answer: 'لا. أنت وحدك من يشغّل Sokuji. الطرف الآخر يسمع فقط ميكروفونًا يتحدث بلغته.',
      },
      {
        question: 'هل يعمل هذا مع تطبيق Teams لسطح المكتب؟',
        answer:
          'نعم. يعمل تطبيق Sokuji لسطح المكتب مع أي تطبيق اجتماعات يتيح لك اختيار الميكروفون، بما في ذلك تطبيقَي Teams وZoom لسطح المكتب.',
      },
      {
        question: 'الطرف الآخر لا يسمع شيئًا. ماذا أتحقق منه؟',
        answer:
          'تحقّق من أن ميكروفون تطبيق الاجتماع هو CABLE Output (VB-Audio Virtual Cable)، وأن «الترجمة التي يسمعها الطرف الآخر» مفعّلة في Sokuji، وأن Sokuji في وضع أنا أو الكل مع جلسة جارية.',
      },
      {
        question: 'كلام الطرف الآخر لا يُترجَم. ماذا أتحقق منه؟',
        answer:
          'تحقّق من أن «صوت الآخر» مضبوط على تطبيق الاجتماع أو على System Audio (All Applications)، وأن Sokuji في وضع الكل والطرف الآخر «في اجتماع»، وأن المكالمة تشغّل صوتًا فعلًا.',
      },
      {
        question: 'أقرأ ترجمة الطرف الآخر لكنني لا أسمعها. ماذا أتحقق منه؟',
        answer:
          'تحقّق من أن «الترجمة التي أسمعها» مفعّلة وتُشغَّل على الجهاز الذي تستمع إليه، وأن «صوت الآخر» هو تطبيق الاجتماع وليس System Audio (All Applications). أوقف الجلسة لتغيير هذا المفتاح.',
      },
      {
        question: 'هل تكلّف الترجمة في الاتجاهين ضعف السعر؟',
        answer:
          'تقريبًا، في الإعداد الافتراضي: يشغّل وضع الكل جلسة منفصلة لكل اتجاه، وهو الأنسب عندما يتحدث الشخصان في الوقت نفسه. أما مع محرك Soniox، فتفعيل «جلسة مشتركة في وضع الكل» (الإعدادات ← متقدم ← المزود) يترجم الطرفين في جلسة واحدة بتكلفة أقل؛ ويُبقي محرك Qwen الجلستين دائمًا. وقراءة ترجمة الطرف الآخر بصوت مسموع تضيف صوتًا ثانيًا فوق ذلك. يشرح دليل الرصيد والتكاليف التفاصيل.',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content:
      'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Tradução de voz bidirecional no Teams, no Zoom e em outros aplicativos de reunião',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'Este guia configura o aplicativo de desktop do Sokuji para uma videochamada com alguém que fala outro idioma. A outra pessoa ouve o que você diz, falado no idioma dela. Você ouve o que ela diz, traduzido para o seu idioma e lido em voz alta, e pode ler os dois lados como legendas. Ele usa a KizunaAI (Com tecnologia Soniox; o mesmo vale para o mecanismo Qwen) e funciona com qualquer aplicativo de reunião que permita escolher um microfone, incluindo os aplicativos de desktop do Microsoft Teams e do Zoom. As capturas de tela são do Sokuji 0.44.0 no Windows com o Microsoft Teams.',
  },
  steps: [
    {
      title: 'Configure o Sokuji com a KizunaAI',
      content:
        'Se ainda não fez isso, siga o <a href="/docs/kizuna-ai/quick-start">início rápido com KizunaAI</a> para instalar o Sokuji e entrar na sua conta.',
    },
    {
      title: 'Escolha o cenário bidirecional',
      content:
        'No guia de configuração, escolha <strong>Conversa on-line em dois sentidos</strong>. Isso coloca o Sokuji no modo <strong>Ambos</strong>, fala a sua tradução na reunião e lê para você a tradução da outra pessoa. Se você já concluiu a configuração, clique em vez disso em <strong>Ambos</strong> no seletor de modo, no canto inferior esquerdo da janela principal. Na primeira vez, um painel pergunta onde está a outra pessoa: mantenha <strong>Em uma reunião</strong>. Clicar em Ambos não ativa <strong>Tradução que eu ouço</strong>; você faz isso na etapa 5.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Defina os dois idiomas',
      content:
        'Em Configurações, em <strong>Idiomas de Tradução</strong>, defina <strong>eu falo</strong> como o seu idioma e <strong>eles ouvem</strong> como o da outra pessoa. A linha abaixo mostra a outra direção: ela diz “Eles falam Inglês → eu leio Chinês” até o Sokuji escutar um único aplicativo, como o Teams (passo 5), e depois “→ eu ouço Chinês”, porque a tradução do outro lado é lida em voz alta para você. Por exemplo, para uma chamada com um cliente no Reino Unido: eu falo Chinês, eles ouvem Inglês.',
      screenshot: '02-languages.png',
      tip: 'Não deixe eu falo em Detecção Automática. No modo Ambos, o Sokuji precisa saber o seu idioma para traduzir a outra pessoa para ele, e Iniciar continua desativado até você escolher um.',
    },
    {
      title: 'Instale o cabo de áudio virtual',
      content:
        'Sua voz traduzida chega à reunião por um microfone virtual. No Windows, é o VB-CABLE, um driver gratuito. Enquanto ele não estiver instalado, o Sokuji oferece instalá-lo toda vez que inicia: clique em <strong>Install Now</strong> e reinicie o Windows se for solicitado. A partir daí, o Sokuji envia sozinho a voz traduzida para o VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'No macOS, o instalador do Sokuji adiciona um driver de áudio virtual, e no Linux o Sokuji cria o próprio microfone virtual. O restante deste guia é igual.',
    },
    {
      title: 'Escolha seu microfone, o aplicativo de reunião e o que você ouve',
      content:
        'Ainda em Configurações: em <strong>Microfone</strong>, escolha seu microfone real. Em <strong>Áudio do outro</strong>, escolha seu aplicativo de reunião, aqui o <strong>Microsoft Teams</strong>. Assim o Sokuji escuta só esse aplicativo, o som de outros programas não é traduzido, e você continua ouvindo a voz original da outra pessoa pelo Teams, como sempre. Na seção <strong>Voz</strong>, <strong>Tradução que o outro lado ouve</strong> vai para o microfone virtual, e <strong>Tradução que eu ouço</strong> lê para você a tradução da outra pessoa no dispositivo escolhido ao lado. Desative Tradução que eu ouço se preferir apenas ler.',
      screenshot: '04-devices.png',
      tip: 'Se o seu aplicativo de reunião não estiver na lista, entre primeiro na chamada (muitas vezes um aplicativo só aparece quando reproduz som) e clique no ícone de atualizar. System Audio (All Applications) também funciona, mas aí Tradução que eu ouço fica esmaecida, para que o Sokuji não traduza de novo a própria voz: você apenas lê a tradução da outra pessoa.',
    },
    {
      title: 'Escolha o VB-CABLE como microfone no aplicativo de reunião',
      content:
        'No Microsoft Teams, abra <strong>Configurações → Dispositivos</strong> e defina o microfone como <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. No Zoom, a mesma opção fica em <strong>Configurações → Áudio → Microfone</strong>. Deixe o alto-falante no fone de ouvido ou nas caixas de som que você usa normalmente.',
      tip: 'Seu microfone real é escolhido no Sokuji, não no aplicativo de reunião: o Sokuji escuta você, e o aplicativo de reunião recebe só a voz traduzida.',
    },
    {
      title: 'Comece a traduzir',
      content:
        'Clique em <strong>Iniciar</strong> no Sokuji. Quando você fala, a outra pessoa ouve suas palavras no idioma dela pelo aplicativo de reunião, e o Sokuji mostra os dois lados como legendas. Quando ela fala, você ouve a voz original dela pelo Teams, e em seguida o Sokuji lê a tradução para você no seu idioma.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Dicas para melhores resultados',
    items: [
      'Use fones de ouvido. Caso contrário, seu microfone capta a outra pessoa pelos seus alto-falantes, e o Sokuji a traduz como se fosse você.',
      'Faça antes uma chamada de teste curta, por exemplo com um colega, e confira se ele ouve a voz traduzida.',
      'Faça uma pausa breve no fim de cada frase: a tradução é falada quando você conclui uma ideia.',
      'Ouvir a tradução da outra pessoa em voz alta custa mais. Se as legendas bastarem, desative Tradução que eu ouço antes de iniciar: essa opção não pode mudar enquanto uma sessão está em andamento.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'A outra pessoa precisa instalar alguma coisa?',
        answer: 'Não. Só você usa o Sokuji. A outra pessoa apenas ouve um microfone que fala o idioma dela.',
      },
      {
        question: 'Isso funciona com o aplicativo de desktop do Teams?',
        answer:
          'Sim. O aplicativo de desktop do Sokuji funciona com qualquer aplicativo de reunião que permita escolher um microfone, incluindo os aplicativos de desktop do Teams e do Zoom.',
      },
      {
        question: 'A outra pessoa não ouve nada. O que devo verificar?',
        answer:
          'Verifique se o microfone do aplicativo de reunião é CABLE Output (VB-Audio Virtual Cable), se Tradução que o outro lado ouve está ativada no Sokuji e se o Sokuji está no modo Eu ou Ambos com uma sessão em andamento.',
      },
      {
        question: 'As palavras da outra pessoa não são traduzidas. O que devo verificar?',
        answer:
          'Verifique se Áudio do outro está definido como o seu aplicativo de reunião ou como System Audio (All Applications), se o Sokuji está no modo Ambos com a outra pessoa em uma reunião e se a chamada está de fato reproduzindo som.',
      },
      {
        question: 'Eu leio a tradução da outra pessoa, mas não a ouço. O que devo verificar?',
        answer:
          'Verifique se Tradução que eu ouço está ativada e toca no dispositivo que você está escutando, e se Áudio do outro é o seu aplicativo de reunião, e não System Audio (All Applications). Pare a sessão para mudar essa opção.',
      },
      {
        question: 'Traduzir nas duas direções custa o dobro?',
        answer:
          'Por padrão, mais ou menos isso: o modo Ambos executa uma sessão separada para cada direção, o que funciona melhor quando as duas pessoas falam ao mesmo tempo. Com o mecanismo Soniox, ativar Sessão compartilhada no modo Ambos (Configurações → Avançado → Provedor) traduz os dois lados em uma única sessão, com custo menor; o mecanismo Qwen sempre mantém as duas sessões. Ler em voz alta a tradução da outra pessoa acrescenta ainda o custo de uma segunda voz. O guia de saldo e custos traz os detalhes.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Двусторонний голосовой перевод в Teams, Zoom и других приложениях для встреч',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'Это руководство настраивает настольное приложение Sokuji для видеозвонка с человеком, который говорит на другом языке. Собеседник слышит ваши слова на своём языке. Вы слышите его речь, переведённую на ваш язык и зачитанную вслух, а речь обеих сторон можно читать в виде субтитров. Используется KizunaAI (на базе Soniox; движок Qwen работает так же); это работает с любым приложением для встреч, в котором можно выбрать микрофон, включая настольные приложения Microsoft Teams и Zoom. Снимки экрана сделаны в Sokuji 0.44.0 на Windows с Microsoft Teams.',
  },
  steps: [
    {
      title: 'Настройте Sokuji с KizunaAI',
      content:
        'Если вы ещё этого не сделали, следуйте руководству <a href="/docs/kizuna-ai/quick-start">«Быстрый старт с KizunaAI»</a>, чтобы установить Sokuji и войти в учётную запись.',
    },
    {
      title: 'Выберите двусторонний сценарий',
      content:
        'В мастере настройки выберите <strong>Двусторонний онлайн-разговор</strong>. Sokuji переходит в режим <strong>Оба</strong>, произносит ваш перевод во встрече и зачитывает вам перевод собеседника. Если вы уже завершили настройку, вместо этого нажмите <strong>Оба</strong> в переключателе режимов в левом нижнем углу главного окна. В первый раз появится панель с вопросом, где собеседник: оставьте <strong>На встрече</strong>. Нажатие «Оба» не включает <strong>Перевод, который слышу я</strong>; это вы сделаете в шаге 5.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Задайте два языка',
      content:
        'В настройках, в разделе <strong>Языки перевода</strong>, укажите в поле <strong>я говорю на</strong> свой язык, а в поле <strong>собеседник слышит</strong> — язык собеседника. Строка под полями показывает обратное направление: в ней написано «Собеседник говорит на Английский → я читаю Китайский», пока Sokuji не начнёт слушать одно приложение, например Teams (шаг 5), а затем «→ я слышу Китайский», потому что перевод собеседника зачитывается вам вслух. Например, для звонка клиенту из Великобритании: я говорю на китайском, собеседник слышит английский.',
      screenshot: '02-languages.png',
      tip: 'Не оставляйте в поле «я говорю на» значение «Автоопределение». В режиме «Оба» Sokuji должен знать ваш язык, чтобы переводить на него собеседника, и кнопка «Старт» остаётся недоступной, пока вы его не выберете.',
    },
    {
      title: 'Установите виртуальный аудиокабель',
      content:
        'Ваш переведённый голос попадает во встречу через виртуальный микрофон. На Windows это VB-CABLE, бесплатный драйвер. Пока он не установлен, Sokuji при каждом запуске предлагает его установить: нажмите <strong>Install Now</strong> и перезапустите Windows, если система попросит. После этого Sokuji сам отправляет переведённый голос в VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'На macOS установщик Sokuji добавляет виртуальный аудиодрайвер, а на Linux Sokuji создаёт собственный виртуальный микрофон. Остальная часть руководства та же.',
    },
    {
      title: 'Выберите микрофон, приложение для встреч и то, что вы слышите',
      content:
        'Там же, в настройках: в разделе <strong>Микрофон</strong> выберите свой настоящий микрофон. В разделе <strong>Аудио собеседника</strong> выберите приложение для встреч, здесь <strong>Microsoft Teams</strong>. Тогда Sokuji слушает только это приложение, и звук других программ не переводится, а собственный голос собеседника вы по-прежнему слышите через Teams, как обычно. В блоке <strong>Речь</strong> параметр <strong>Перевод, который слышит собеседник</strong> выводится в виртуальный микрофон, а <strong>Перевод, который слышу я</strong> зачитывает вам перевод собеседника на устройстве, выбранном рядом с ним. Выключите «Перевод, который слышу я», если хотите только читать перевод.',
      screenshot: '04-devices.png',
      tip: 'Если вашего приложения для встреч нет в списке, сначала подключитесь к звонку (приложение часто появляется в списке только после того, как начнёт воспроизводить звук) и нажмите значок обновления. System Audio (All Applications) тоже подходит, но тогда «Перевод, который слышу я» становится неактивным, чтобы Sokuji не переводил повторно собственный голос: речь собеседника вы только читаете.',
    },
    {
      title: 'Выберите VB-CABLE как микрофон в приложении для встреч',
      content:
        'В Microsoft Teams откройте <strong>Настройки → Устройства</strong> (Settings → Devices) и выберите в качестве микрофона <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. В Zoom та же настройка находится в разделе <strong>Настройки → Звук → Микрофон</strong> (Settings → Audio → Microphone). Динамик оставьте на обычной гарнитуре или колонках.',
      tip: 'Настоящий микрофон выбирается в Sokuji, а не в приложении для встреч: Sokuji слушает вас, а приложение для встреч получает только переведённый голос.',
    },
    {
      title: 'Начните перевод',
      content:
        'Нажмите <strong>Старт</strong> в Sokuji. Когда вы говорите, собеседник слышит ваши слова на своём языке через приложение для встреч, а Sokuji показывает речь обеих сторон в виде субтитров. Когда говорит собеседник, вы слышите его собственный голос через Teams, а затем Sokuji зачитывает вам перевод на вашем языке.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Советы для лучших результатов',
    items: [
      'Надевайте наушники. Иначе микрофон улавливает собеседника из динамиков, и Sokuji переводит его так, будто это говорите вы.',
      'Сначала сделайте короткий тестовый звонок, например коллеге, и проверьте, что он слышит переведённый голос.',
      'Делайте короткую паузу в конце каждого предложения: перевод озвучивается, когда вы заканчиваете мысль.',
      'Зачитывание речи собеседника вслух стоит дополнительно. Если субтитров достаточно, выключите «Перевод, который слышу я» до начала сеанса: во время сеанса его нельзя изменить.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Нужно ли собеседнику что-то устанавливать?',
        answer: 'Нет. Sokuji запускаете только вы. Собеседник просто слышит микрофон, который говорит на его языке.',
      },
      {
        question: 'Работает ли это с настольным приложением Teams?',
        answer:
          'Да. Настольное приложение Sokuji работает с любым приложением для встреч, в котором можно выбрать микрофон, включая настольные приложения Teams и Zoom.',
      },
      {
        question: 'Собеседник ничего не слышит. Что проверить?',
        answer:
          'Проверьте, что в приложении для встреч выбран микрофон CABLE Output (VB-Audio Virtual Cable), что в Sokuji включён «Перевод, который слышит собеседник» и что Sokuji работает в режиме «Я» или «Оба» с запущенным сеансом.',
      },
      {
        question: 'Слова собеседника не переводятся. Что проверить?',
        answer:
          'Проверьте, что в «Аудио собеседника» выбрано ваше приложение для встреч или System Audio (All Applications), что Sokuji работает в режиме «Оба», где для собеседника выбрано «На встрече», и что звонок действительно воспроизводит звук.',
      },
      {
        question: 'Я читаю перевод собеседника, но не слышу его. Что проверить?',
        answer:
          'Проверьте, что «Перевод, который слышу я» включён и воспроизводится на устройстве, которое вы слушаете, и что в «Аудио собеседника» выбрано ваше приложение для встреч, а не System Audio (All Applications). Чтобы изменить этот переключатель, остановите сеанс.',
      },
      {
        question: 'Стоит ли перевод в обе стороны вдвое дороже?',
        answer:
          'По умолчанию — примерно да: в режиме «Оба» для каждого направления запускается отдельный сеанс, что лучше всего справляется с одновременной речью обоих собеседников. С движком Soniox, если включить «Общий сеанс в режиме «Оба»» (Настройки → Расширенный → Поставщик), обе стороны переводятся в одном сеансе и дешевле; движок Qwen всегда сохраняет два сеанса. Зачитывание перевода собеседника вслух добавляет к этому второй голос. Подробности — в руководстве по оплате.',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'Zweiseitige Sprachübersetzung in Teams, Zoom und anderen Meeting-Apps',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'Diese Anleitung richtet die Sokuji-Desktop-App für einen Videoanruf mit jemandem ein, der eine andere Sprache spricht. Ihr Gegenüber hört, was Sie sagen, in seiner Sprache. Sie hören, was es sagt, in Ihre Sprache übersetzt und vorgelesen, und Sie können beide Seiten als Untertitel lesen. Verwendet wird KizunaAI (Unterstützt von Soniox; die Qwen-Engine funktioniert genauso); es funktioniert mit jeder Meeting-App, in der Sie ein Mikrofon auswählen können, einschließlich der Desktop-Apps von Microsoft Teams und Zoom. Die Screenshots stammen aus Sokuji 0.44.0 unter Windows mit Microsoft Teams.',
  },
  steps: [
    {
      title: 'Sokuji mit KizunaAI einrichten',
      content:
        'Falls noch nicht geschehen, folgen Sie dem <a href="/docs/kizuna-ai/quick-start">KizunaAI-Schnellstart</a>, um Sokuji zu installieren und sich anzumelden.',
    },
    {
      title: 'Das zweiseitige Szenario wählen',
      content:
        'Wählen Sie in der Einrichtung <strong>Zweiseitiges Online-Gespräch</strong>. Damit wechselt Sokuji in den Modus <strong>Beides</strong>, spricht Ihre Übersetzung ins Meeting und liest Ihnen die Übersetzung der anderen Seite vor. Wenn Sie die Einrichtung bereits abgeschlossen haben, klicken Sie stattdessen im Modusschalter unten links im Hauptfenster auf <strong>Beides</strong>. Beim ersten Mal fragt ein Panel, wo das Gegenüber ist: Behalten Sie <strong>In einem Meeting</strong> bei. Ein Klick auf „Beides“ schaltet <strong>Übersetzung, die ich höre</strong> nicht ein; das erledigen Sie in Schritt 5.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Die beiden Sprachen einstellen',
      content:
        'Stellen Sie in den Einstellungen unter <strong>Übersetzungssprachen</strong> bei <strong>Ich spreche</strong> Ihre eigene Sprache ein und bei <strong>sie hören</strong> die Ihres Gegenübers. Die Zeile darunter zeigt die Gegenrichtung: Sie lautet „Sie sprechen Englisch → ich lese Chinesisch“, bis Sokuji nur noch einer App wie Teams zuhört (Schritt 5), danach „→ ich höre Chinesisch“, weil Ihnen die Übersetzung der anderen Seite vorgelesen wird. Für einen Anruf mit einem Kunden in Großbritannien zum Beispiel: Ich spreche Chinesisch, sie hören Englisch.',
      screenshot: '02-languages.png',
      tip: 'Lassen Sie „Ich spreche“ nicht auf „Automatische Erkennung“. Im Modus „Beides“ muss Sokuji Ihre Sprache kennen, um die andere Seite in sie zu übersetzen, und „Starten“ bleibt deaktiviert, bis Sie eine auswählen.',
    },
    {
      title: 'Das virtuelle Audiokabel installieren',
      content:
        'Ihre übersetzte Stimme gelangt über ein virtuelles Mikrofon ins Meeting. Unter Windows ist das VB-CABLE, ein kostenloser Treiber. Solange er fehlt, fragt Sokuji bei jedem Start, ob er installiert werden soll: Klicken Sie auf <strong>Install Now</strong> und starten Sie Windows neu, falls Sie dazu aufgefordert werden. Danach sendet Sokuji seine übersetzte Stimme automatisch an VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'Unter macOS fügt das Sokuji-Installationsprogramm einen virtuellen Audiotreiber hinzu, und unter Linux erstellt Sokuji ein eigenes virtuelles Mikrofon. Der Rest dieser Anleitung ist gleich.',
    },
    {
      title: 'Mikrofon, Meeting-App und das, was Sie hören, wählen',
      content:
        'Weiterhin in den Einstellungen: Wählen Sie unter <strong>Mikrofon</strong> Ihr echtes Mikrofon. Wählen Sie unter <strong>Audio des Gegenübers</strong> Ihre Meeting-App, hier <strong>Microsoft Teams</strong>. Sokuji hört dann nur diese App ab, sodass Ton aus anderen Programmen nicht übersetzt wird, und die Originalstimme Ihres Gegenübers hören Sie wie gewohnt weiter über Teams. Im Bereich <strong>Sprache</strong> geht <strong>Übersetzung, die das Gegenüber hört</strong> an das virtuelle Mikrofon, und <strong>Übersetzung, die ich höre</strong> liest Ihnen die Übersetzung Ihres Gegenübers auf dem daneben gewählten Gerät vor. Schalten Sie „Übersetzung, die ich höre“ aus, wenn Sie sie lieber nur lesen möchten.',
      screenshot: '04-devices.png',
      tip: 'Wenn Ihre Meeting-App nicht in der Liste steht, treten Sie zuerst dem Anruf bei (eine App erscheint oft erst, wenn sie Ton wiedergibt) und klicken Sie auf das Aktualisieren-Symbol. System Audio (All Applications) funktioniert auch, dann ist „Übersetzung, die ich höre“ jedoch ausgegraut, damit Sokuji seine eigene Stimme nicht erneut übersetzt: Sie lesen die andere Seite dann nur.',
    },
    {
      title: 'VB-CABLE in der Meeting-App als Mikrofon wählen',
      content:
        'Öffnen Sie in Microsoft Teams <strong>Einstellungen → Geräte</strong> und stellen Sie das Mikrofon auf <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. In Zoom finden Sie dieselbe Einstellung unter <strong>Einstellungen → Audio → Mikrofon</strong>. Lassen Sie den Lautsprecher auf Ihrem üblichen Headset oder Ihren Lautsprechern.',
      tip: 'Ihr echtes Mikrofon wählen Sie in Sokuji aus, nicht in der Meeting-App: Sokuji hört Ihnen zu, und die Meeting-App erhält nur die übersetzte Stimme.',
    },
    {
      title: 'Übersetzung starten',
      content:
        'Klicken Sie in Sokuji auf <strong>Starten</strong>. Wenn Sie sprechen, hört Ihr Gegenüber Ihre Worte über die Meeting-App in seiner Sprache, und Sokuji zeigt beide Seiten als Untertitel an. Wenn Ihr Gegenüber spricht, hören Sie seine Originalstimme über Teams, und danach liest Ihnen Sokuji die Übersetzung in Ihrer Sprache vor.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Tipps für beste Ergebnisse',
    items: [
      'Tragen Sie Kopfhörer. Sonst nimmt Ihr Mikrofon Ihr Gegenüber aus den Lautsprechern auf, und Sokuji übersetzt es, als wären Sie es.',
      'Machen Sie zuerst einen kurzen Testanruf, zum Beispiel mit einer Kollegin oder einem Kollegen, und prüfen Sie, ob die übersetzte Stimme ankommt.',
      'Machen Sie am Ende jedes Satzes eine kurze Pause: Die Übersetzung wird gesprochen, sobald Sie einen Gedanken abgeschlossen haben.',
      'Das Vorlesen der anderen Seite kostet extra. Wenn Ihnen Untertitel genügen, schalten Sie „Übersetzung, die ich höre“ vor dem Start aus: Während eine Sitzung läuft, lässt sich das nicht ändern.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Muss mein Gegenüber etwas installieren?',
        answer: 'Nein. Nur Sie verwenden Sokuji. Ihr Gegenüber hört einfach ein Mikrofon, das seine Sprache spricht.',
      },
      {
        question: 'Funktioniert das mit der Teams-Desktop-App?',
        answer:
          'Ja. Die Sokuji-Desktop-App funktioniert mit jeder Meeting-App, in der Sie ein Mikrofon auswählen können, einschließlich der Desktop-Apps von Teams und Zoom.',
      },
      {
        question: 'Mein Gegenüber hört nichts. Was sollte ich prüfen?',
        answer:
          'Prüfen Sie, ob das Mikrofon der Meeting-App CABLE Output (VB-Audio Virtual Cable) ist, ob „Übersetzung, die das Gegenüber hört“ in Sokuji eingeschaltet ist und ob Sokuji im Modus „Ich“ oder „Beides“ mit laufender Sitzung ist.',
      },
      {
        question: 'Die Worte meines Gegenübers werden nicht übersetzt. Was sollte ich prüfen?',
        answer:
          'Prüfen Sie, ob „Audio des Gegenübers“ auf Ihre Meeting-App oder auf System Audio (All Applications) eingestellt ist, ob Sokuji im Modus „Beides“ ist, mit dem Gegenüber „In einem Meeting“, und ob der Anruf tatsächlich Ton wiedergibt.',
      },
      {
        question: 'Ich lese die Übersetzung meines Gegenübers, höre sie aber nicht. Was sollte ich prüfen?',
        answer:
          'Prüfen Sie, ob „Übersetzung, die ich höre“ eingeschaltet ist und auf dem Gerät wiedergegeben wird, über das Sie hören, und ob „Audio des Gegenübers“ Ihre Meeting-App ist statt System Audio (All Applications). Beenden Sie die Sitzung, um den Schalter zu ändern.',
      },
      {
        question: 'Kostet die Übersetzung in beide Richtungen doppelt so viel?',
        answer:
          'Standardmäßig ungefähr ja: Der Modus „Beides“ führt für jede Richtung eine eigene Sitzung aus, was am besten funktioniert, wenn beide gleichzeitig sprechen. Mit der Soniox-Engine werden beide Seiten, wenn Sie »Gemeinsame Sitzung im Modus „Beides“« (Einstellungen → Erweitert → Anbieter) einschalten, in einer Sitzung zu geringeren Kosten übersetzt; die Qwen-Engine behält immer die zwei Sitzungen. Das Vorlesen der anderen Seite kommt als zweite Stimme noch hinzu. Details finden Sie in der Anleitung zu Guthaben und Kosten.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Двосторонній голосовий переклад у Teams, Zoom та інших застосунках для зустрічей',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'Цей посібник налаштовує настільний застосунок Sokuji для відеодзвінка з людиною, яка говорить іншою мовою. Співрозмовник чує ваші слова своєю мовою. Ви чуєте його мовлення, перекладене вашою мовою й зачитане вголос, а мовлення обох сторін можете читати у вигляді субтитрів. Використовується KizunaAI (на базі Soniox; рушій Qwen працює так само); це працює з будь-яким застосунком для зустрічей, у якому можна вибрати мікрофон, зокрема з настільними застосунками Microsoft Teams і Zoom. Знімки екрана зроблено в Sokuji 0.44.0 на Windows з Microsoft Teams.',
  },
  steps: [
    {
      title: 'Налаштуйте Sokuji з KizunaAI',
      content:
        'Якщо ви ще цього не зробили, дотримуйтеся посібника <a href="/docs/kizuna-ai/quick-start">«Швидкий старт із KizunaAI»</a>, щоб установити Sokuji та увійти в обліковий запис.',
    },
    {
      title: 'Виберіть двосторонній сценарій',
      content:
        'У майстрі налаштування виберіть <strong>Двостороння онлайн-розмова</strong>. Sokuji переходить у режим <strong>Обидва</strong>, озвучує ваш переклад на зустрічі й зачитує вам переклад співрозмовника. Якщо ви вже завершили налаштування, натомість натисніть <strong>Обидва</strong> в перемикачі режимів у лівому нижньому куті головного вікна. Першого разу панель запитає, де співрозмовник: залиште <strong>На зустрічі</strong>. Натискання «Обидва» не вмикає <strong>Переклад, який чую я</strong>; це ви зробите на кроці 5.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Задайте дві мови',
      content:
        'У налаштуваннях, у розділі <strong>Мови перекладу</strong>, укажіть у полі <strong>Я говорю</strong> свою мову, а в полі <strong>вони чують</strong> — мову співрозмовника. Рядок під полями показує зворотний напрямок: «Вони говорять Англійська → я читаю Китайська», доки Sokuji не почне слухати один застосунок, наприклад Teams (крок 5), а потім «→ я чую Китайська», бо переклад співрозмовника зачитується вам уголос. Наприклад, для дзвінка клієнтові з Великої Британії: Я говорю китайською, вони чують англійською.',
      screenshot: '02-languages.png',
      tip: 'Не залишайте в полі «Я говорю» значення «Автовиявлення». У режимі «Обидва» Sokuji має знати вашу мову, щоб перекладати нею співрозмовника, і кнопка «Почати» залишається недоступною, доки ви її не виберете.',
    },
    {
      title: 'Установіть віртуальний аудіокабель',
      content:
        'Ваш перекладений голос потрапляє на зустріч через віртуальний мікрофон. На Windows це VB-CABLE, безкоштовний драйвер. Поки його не встановлено, Sokuji під час кожного запуску пропонує його встановити: натисніть <strong>Install Now</strong> і перезапустіть Windows, якщо система попросить. Після цього Sokuji сам надсилає перекладений голос у VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'На macOS інсталятор Sokuji додає віртуальний аудіодрайвер, а на Linux Sokuji створює власний віртуальний мікрофон. Решта посібника така сама.',
    },
    {
      title: 'Виберіть мікрофон, застосунок для зустрічей і те, що ви чуєте',
      content:
        'Там само, у налаштуваннях: у розділі <strong>Мікрофон</strong> виберіть свій справжній мікрофон. У розділі <strong>Аудіо співрозмовника</strong> виберіть застосунок для зустрічей, тут <strong>Microsoft Teams</strong>. Тоді Sokuji слухає лише цей застосунок, тож звук інших програм не перекладається, а власний голос співрозмовника ви й далі чуєте через Teams, як зазвичай. У блоці <strong>Мовлення</strong> <strong>Переклад, який чує співрозмовник</strong> надходить на віртуальний мікрофон, а <strong>Переклад, який чую я</strong> зачитує вам переклад співрозмовника на пристрої, вибраному поруч. Вимкніть «Переклад, який чую я», якщо хочете лише читати переклад.',
      screenshot: '04-devices.png',
      tip: 'Якщо вашого застосунку для зустрічей немає в списку, спочатку приєднайтеся до дзвінка (застосунок часто з’являється в списку лише тоді, коли починає відтворювати звук) і натисніть значок оновлення. System Audio (All Applications) теж підходить, але тоді «Переклад, який чую я» стає неактивним, щоб Sokuji не перекладав повторно власний голос: мовлення співрозмовника ви лише читаєте.',
    },
    {
      title: 'Виберіть VB-CABLE як мікрофон у застосунку для зустрічей',
      content:
        'У Microsoft Teams відкрийте <strong>Налаштування → Пристрої</strong> (Settings → Devices) і виберіть як мікрофон <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. У Zoom те саме налаштування розташоване в розділі <strong>Налаштування → Аудіо → Мікрофон</strong> (Settings → Audio → Microphone). Динамік залиште на звичайній гарнітурі чи колонках.',
      tip: 'Справжній мікрофон вибирається в Sokuji, а не в застосунку для зустрічей: Sokuji слухає вас, а застосунок для зустрічей отримує лише перекладений голос.',
    },
    {
      title: 'Почніть переклад',
      content:
        'Натисніть <strong>Почати</strong> в Sokuji. Коли ви говорите, співрозмовник чує ваші слова своєю мовою через застосунок для зустрічей, а Sokuji показує мовлення обох сторін у вигляді субтитрів. Коли говорить співрозмовник, ви чуєте його власний голос через Teams, а потім Sokuji зачитує вам переклад вашою мовою.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Поради для найкращих результатів',
    items: [
      'Надягайте навушники. Інакше мікрофон уловлює співрозмовника з динаміків, і Sokuji перекладає його так, ніби це говорите ви.',
      'Спершу зробіть короткий тестовий дзвінок, наприклад колезі, і перевірте, що він чує перекладений голос.',
      'Робіть коротку паузу в кінці кожного речення: переклад озвучується, коли ви завершуєте думку.',
      'Зачитування співрозмовника вголос коштує додатково. Якщо субтитрів достатньо, вимкніть «Переклад, який чую я» перед початком: під час сеансу цей параметр змінити не можна.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Чи потрібно співрозмовнику щось установлювати?',
        answer: 'Ні. Sokuji запускаєте лише ви. Співрозмовник просто чує мікрофон, який говорить його мовою.',
      },
      {
        question: 'Чи працює це з настільним застосунком Teams?',
        answer:
          'Так. Настільний застосунок Sokuji працює з будь-яким застосунком для зустрічей, у якому можна вибрати мікрофон, зокрема з настільними застосунками Teams і Zoom.',
      },
      {
        question: 'Співрозмовник нічого не чує. Що перевірити?',
        answer:
          'Перевірте, що в застосунку для зустрічей вибрано мікрофон CABLE Output (VB-Audio Virtual Cable), що в Sokuji ввімкнено «Переклад, який чує співрозмовник» і що Sokuji працює в режимі «Я» або «Обидва» із запущеним сеансом.',
      },
      {
        question: 'Слова співрозмовника не перекладаються. Що перевірити?',
        answer:
          'Перевірте, що в «Аудіо співрозмовника» вибрано ваш застосунок для зустрічей або System Audio (All Applications), що Sokuji працює в режимі «Обидва», де співрозмовник на зустрічі, і що дзвінок справді відтворює звук.',
      },
      {
        question: 'Я читаю переклад співрозмовника, але не чую його. Що перевірити?',
        answer:
          'Перевірте, що «Переклад, який чую я» ввімкнено й він відтворюється на пристрої, який ви слухаєте, і що в «Аудіо співрозмовника» вибрано ваш застосунок для зустрічей, а не System Audio (All Applications). Щоб змінити цей перемикач, зупиніть сеанс.',
      },
      {
        question: 'Чи коштує переклад в обидва боки вдвічі дорожче?',
        answer:
          'За замовчуванням — приблизно так: у режимі «Обидва» для кожного напрямку запускається окремий сеанс — це найкраще підходить, коли обоє говорять одночасно. З рушієм Soniox, якщо ввімкнути «Спільний сеанс у режимі «Обидва»» (Налаштування → Розширений → Постачальник), обидві сторони перекладаються в одному сеансі дешевше; рушій Qwen завжди зберігає два сеанси. Зачитування співрозмовника вголос додає ще й другий голос. Докладніше — у посібнику про баланс і витрати.',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Traduzione vocale bidirezionale in Teams, Zoom e altre app per riunioni',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'Questa guida configura l’app desktop di Sokuji per una videochiamata con qualcuno che parla un’altra lingua. L’altra persona sente ciò che dici, pronunciato nella sua lingua. Tu senti ciò che dice, tradotto nella tua e letto ad alta voce, e puoi leggere entrambe le parti come sottotitoli. Usa KizunaAI (Con tecnologia Soniox; il motore Qwen funziona allo stesso modo) e funziona con qualsiasi app per riunioni che ti permetta di scegliere un microfono, comprese le app desktop di Microsoft Teams e Zoom. Le schermate provengono da Sokuji 0.44.0 su Windows con Microsoft Teams.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content:
        'Se non l’hai ancora fatto, segui la <a href="/docs/kizuna-ai/quick-start">guida rapida a KizunaAI</a> per installare Sokuji e accedere.',
    },
    {
      title: 'Scegli lo scenario bidirezionale',
      content:
        'Nella configurazione guidata, scegli <strong>Conversazione online bidirezionale</strong>. Sokuji passa in modalità <strong>Entrambi</strong>, pronuncia la tua traduzione nella riunione e ti legge la traduzione dell’altra parte. Se hai già completato la configurazione, fai invece clic su <strong>Entrambi</strong> nel selettore di modalità in basso a sinistra nella finestra principale. La prima volta, un pannello ti chiede dov’è l’altra parte: lascia <strong>In una riunione</strong>. Il clic su Entrambi non attiva <strong>Traduzione che sento io</strong>; lo fai al passaggio 5.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Imposta le due lingue',
      content:
        'In Impostazioni, sotto <strong>Lingue di Traduzione</strong>, imposta <strong>Io parlo</strong> sulla tua lingua e <strong>loro sentono</strong> su quella dell’altra persona. La riga sottostante mostra l’altra direzione: dice «Loro parlano Inglese → io leggo Cinese» finché Sokuji non ascolta una sola app, come Teams (passaggio 5), poi «→ io sento Cinese», perché la traduzione dell’altra parte ti viene letta ad alta voce. Per esempio, per una chiamata con un cliente nel Regno Unito: Io parlo cinese, loro sentono inglese.',
      screenshot: '02-languages.png',
      tip: 'Non lasciare Io parlo su Rilevamento Automatico. In modalità Entrambi, Sokuji deve conoscere la tua lingua per tradurvi l’altra parte, e Avvia resta disattivato finché non ne scegli una.',
    },
    {
      title: 'Installa il cavo audio virtuale',
      content:
        'La tua voce tradotta arriva alla riunione tramite un microfono virtuale. Su Windows è VB-CABLE, un driver gratuito. Finché manca, Sokuji propone di installarlo a ogni avvio: fai clic su <strong>Install Now</strong> e riavvia Windows se richiesto. Da quel momento Sokuji invia da solo la sua voce tradotta a VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'Su macOS il programma di installazione di Sokuji aggiunge un driver audio virtuale, e su Linux Sokuji crea un proprio microfono virtuale. Il resto di questa guida è identico.',
    },
    {
      title: 'Scegli il microfono, l’app per riunioni e cosa senti',
      content:
        'Sempre in Impostazioni: in <strong>Microfono</strong>, scegli il tuo microfono reale. In <strong>Audio dell\'altro</strong>, scegli la tua app per riunioni, qui <strong>Microsoft Teams</strong>. Così Sokuji ascolta solo quell’app, e il suono degli altri programmi non viene tradotto. Continui a sentire la voce originale dell’altra persona tramite Teams, come sempre. Nella sezione <strong>Voce</strong>, <strong>Traduzione che sente l\'altra parte</strong> va al microfono virtuale, e <strong>Traduzione che sento io</strong> ti legge la traduzione dell’altra persona sul dispositivo indicato accanto. Disattiva Traduzione che sento io se preferisci solo leggerla.',
      screenshot: '04-devices.png',
      tip: 'Se la tua app per riunioni non è nell’elenco, entra prima nella chiamata (spesso un’app compare solo quando riproduce audio) e fai clic sull’icona di aggiornamento. Funziona anche System Audio (All Applications), ma in quel caso Traduzione che sento io appare in grigio, così Sokuji non traduce di nuovo la propria voce: l’altra parte la puoi solo leggere.',
    },
    {
      title: 'Scegli VB-CABLE come microfono nell’app per riunioni',
      content:
        'In Microsoft Teams, apri <strong>Impostazioni → Dispositivi</strong> e imposta il microfono su <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. In Zoom la stessa impostazione si trova in <strong>Impostazioni → Audio → Microfono</strong>. Lascia l’altoparlante sulle cuffie o sugli altoparlanti che usi di solito.',
      tip: 'Il microfono reale si sceglie in Sokuji, non nell’app per riunioni: Sokuji ascolta te, e l’app per riunioni riceve solo la voce tradotta.',
    },
    {
      title: 'Inizia a tradurre',
      content:
        'Fai clic su <strong>Avvia</strong> in Sokuji. Quando parli, l’altra parte sente le tue parole nella sua lingua tramite l’app per riunioni, e Sokuji mostra entrambe le parti come sottotitoli. Quando parla l’altra persona, senti la sua voce originale tramite Teams, poi Sokuji ti legge la traduzione nella tua lingua.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Consigli per risultati migliori',
    items: [
      'Indossa le cuffie. Altrimenti il microfono capta l’altra persona dai tuoi altoparlanti, e Sokuji la traduce come se fossi tu.',
      'Fai prima una breve chiamata di prova, per esempio con un collega, e verifica che senta la voce tradotta.',
      'Fai una breve pausa alla fine di ogni frase: la traduzione viene pronunciata quando concludi un pensiero.',
      'Sentire l’altra parte letta ad alta voce costa di più. Se ti bastano i sottotitoli, disattiva Traduzione che sento io prima di avviare: non si può cambiare mentre una sessione è attiva.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'L’altra persona deve installare qualcosa?',
        answer: 'No. Solo tu usi Sokuji. L’altra persona sente semplicemente un microfono che parla la sua lingua.',
      },
      {
        question: 'Funziona con l’app desktop di Teams?',
        answer:
          'Sì. L’app desktop di Sokuji funziona con qualsiasi app per riunioni che ti permetta di scegliere un microfono, comprese le app desktop di Teams e Zoom.',
      },
      {
        question: 'L’altra persona non sente niente. Cosa devo controllare?',
        answer:
          'Controlla che il microfono dell’app per riunioni sia CABLE Output (VB-Audio Virtual Cable), che Traduzione che sente l\'altra parte sia attivata in Sokuji e che Sokuji sia in modalità Io o Entrambi con una sessione attiva.',
      },
      {
        question: 'Le parole dell’altra persona non vengono tradotte. Cosa devo controllare?',
        answer:
          'Controlla che Audio dell\'altro sia impostato sulla tua app per riunioni o su System Audio (All Applications), che Sokuji sia in modalità Entrambi con l’altra parte in una riunione e che la chiamata stia effettivamente riproducendo audio.',
      },
      {
        question: 'Leggo la traduzione dell’altra parte ma non la sento. Cosa devo controllare?',
        answer:
          'Controlla che Traduzione che sento io sia attivata e venga riprodotta sul dispositivo che ascolti, e che Audio dell\'altro sia la tua app per riunioni e non System Audio (All Applications). Per cambiare l’interruttore, interrompi la sessione.',
      },
      {
        question: 'Tradurre in entrambe le direzioni costa il doppio?',
        answer:
          'All’incirca sì, per impostazione predefinita: la modalità Entrambi esegue una sessione separata per ogni direzione, che regge meglio quando entrambe le persone parlano insieme. Con il motore Soniox, attivando Sessione condivisa in modalità Entrambi (Impostazioni → Avanzato → Fornitore), entrambe le parti vengono tradotte in una sola sessione a un costo inferiore; il motore Qwen mantiene sempre le due sessioni. Leggere ad alta voce l’altra parte aggiunge in più una seconda voce. Trovi i dettagli nella guida su saldo e costi.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function TwoWayMeetingTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/two-way-meeting" />;
}
